/**
 * CountryFundingChartV2 - Simplified version using withChartConfiguration HOC
 * Demonstrates ROSIE principle: maximum reusability with minimal code
 */

import React, { useState } from 'react';
import { UnifiedChartFactory } from '../services/UnifiedChartFactory';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import { formatMonetaryValue } from '../utils/chartUtils';
import { Download, FileSpreadsheet, Pencil } from 'lucide-react';
import { countryFundingToCSV, downloadCSV, generateExportFilename } from '../utils/exportUtils';
import { dataValidationService } from '../services/DataValidationService';
import { ChartContainer } from '../utils/ContainerStyles';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { ChartMetadata } from './ChartMetadata';

// Country data interface
export interface CountryFundingData {
  week: string;
  countries: {
    country_name: string;
    country_code: string;
    country_flag?: string; // Flag emoji from Airtable
    lat: number;
    lng: number;
    funding_amount: number;
    deal_count: number;
  }[];
  continents?: {
    continent_name: string;
    funding_amount: number;
    deal_count: number;
  }[];
}

// Country flag emoji mapping
const COUNTRY_FLAGS: Record<string, string> = {
  'United States': '🇺🇸',
  'United Kingdom': '🇬🇧',
  'Israel': '🇮🇱',
  'Germany': '🇩🇪',
  'Canada': '🇨🇦',
  'France': '🇫🇷',
  'Netherlands': '🇳🇱',
  'Switzerland': '🇨🇭',
  'Sweden': '🇸🇪',
  'Australia': '🇦🇺',
  'Singapore': '🇸🇬',
  'India': '🇮🇳',
  'China': '🇨🇳',
  'Japan': '🇯🇵',
  'South Korea': '🇰🇷',
  'South Korea (Republic of Korea)': '🇰🇷',
  'Spain': '🇪🇸',
  'Italy': '🇮🇹',
  'Norway': '🇳🇴',
  'Denmark': '🇩🇰',
  'Finland': '🇫🇮',
  'Ireland': '🇮🇪',
  'Belgium': '🇧🇪',
  'Austria': '🇦🇹',
  'Gibraltar': '🇬🇮',
  'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  'Poland': '🇵🇱',
  'Czechia': '🇨🇿',
  'Portugal': '🇵🇹',
  'Bahamas': '🇧🇸'
};

interface CountryFundingChartProps extends ChartProps {
  data: CountryFundingData;
  weekLabel: string;
  timeframe: string;
  filters?: Record<string, string[] | undefined>;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
}

/**
 * Chart configuration generator
 * This function is called by the HOC to get the ECharts configuration
 */
const getCountryFundingChartOption = (props: CountryFundingChartProps) => {
  const { data, chartContext, timeframe, filters } = props;

  // Check for custom title
  const titleKey: TitleKey = {
    chartType: 'country',
    timeframe,
    filters
  };
  const customTitle = customTitleService.getCustomTitle(titleKey);

  // Process and filter data
  const topCountries = data?.countries
    ? data.countries
        .filter(country => country.funding_amount > 0)
        .sort((a, b) => b.funding_amount - a.funding_amount)
        .slice(0, 10) // Increased to show up to 10 countries
    : [];

  // Calculate totals
  const totalFunding = topCountries.reduce((sum, country) => sum + country.funding_amount, 0);
  // const totalDealCount = topCountries.reduce((sum, c) => sum + c.deal_count, 0);

  // Add flags to data - use flag from API (from Airtable) ONLY, no fallback
  // If API doesn't provide flag, we prefer no flag over incorrect globe emoji
  const enrichedData = topCountries.map(country => {
    if (!country.country_flag) {
      console.error(`Missing country_flag for ${country.country_name} - API should provide flags from Airtable`);
    }
    return {
      ...country,
      flag: country.country_flag || '', // API flag only, empty string if missing (no globe fallback)
      count: country.deal_count // Add count for dynamic title generation
    };
  });

  // Use custom title if exists, otherwise let dynamic generation handle it
  const option = UnifiedChartFactory.createCountryFundingChart(
    enrichedData,
    customTitle?.title || '', // Empty title = dynamic generation
    customTitle?.subtitle || '', // Empty subtitle = dynamic generation
    {
      timeframe: (timeframe as '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom') || '12w',
      chartContext: (chartContext as 'default' | 'email' | 'social-media' | 'presentation') || 'default'
    }
  );

  // Add custom tooltip with percentage
  option.tooltip = {
    ...option.tooltip,
    formatter: (params: any) => {
      const dataIndex = params[0].dataIndex;
      const country = topCountries[dataIndex];
      const percentage = (country.funding_amount / totalFunding * 100).toFixed(1);
      const dealText = country.deal_count === 1 ? '1 deal' : `${country.deal_count} deals`;

      return `
        <div style="font-weight: 600; font-size: 18px; margin-bottom: 8px; color: #633FF3;">
          ${country.country_name}
        </div>
        <div style="font-size: 16px;">
          <div style="margin: 4px 0;">
            <span style="font-weight: 500;">Funding:</span>
            <span style="font-weight: 700; color: #633FF3;">${formatMonetaryValue(country.funding_amount)}</span>
          </div>
          <div style="margin: 4px 0;">
            <span style="font-weight: 500;">Deals:</span>
            <span style="font-weight: 600;">${dealText}</span>
          </div>
          <div style="margin: 4px 0;">
            <span style="font-weight: 500;">Share:</span>
            <span style="font-weight: 600;">${percentage}% of total</span>
          </div>
        </div>
      `;
    }
  };

  return option;
};

/**
 * The actual component - now extremely simple!
 */
const CountryFundingChartComponent: React.FC<CountryFundingChartProps & EnhancedChartProps> = React.memo(({
  data,
  chartRef,
  isLoading,
  error,
  downloadChart,
  refreshChart,
  timeframe,
  filters,
  metadata
}) => {
  // Title editor state - ALL HOOKS MUST BE AT THE TOP
  const [editorOpen, setEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Generate title key
  const titleKey: TitleKey = React.useMemo(() => ({
    chartType: 'country',
    timeframe,
    filters
  }), [timeframe, filters]);

  // Check for custom title - use useMemo to re-compute when forceRefresh changes
  const customTitle = React.useMemo(() =>
    customTitleService.getCustomTitle(titleKey),
    [titleKey, forceRefresh]
  );

  // On mount, check if custom title exists and trigger initial load
  // This ensures custom titles are loaded from localStorage on page refresh
  React.useEffect(() => {
    // Only run once on initial mount
    if (forceRefresh === 0) {
      const hasCustomTitle = customTitleService.hasCustomTitle(titleKey);

      if (hasCustomTitle) {
        // Wait for chart to be ready before refreshing
        const timer = setTimeout(() => {
          setForceRefresh(1);
        }, 200); // Longer delay to ensure chart is fully initialized
        return () => clearTimeout(timer);
      }
    }
  }, [titleKey]); // Only re-run when titleKey changes

  // Refresh chart when forceRefresh changes (after state update completes)
  React.useEffect(() => {
    if (forceRefresh > 0) {
      refreshChart();
    }
  }, [forceRefresh, refreshChart]);

  // Get current title and subtitle from chart option (must be before conditional returns)
  const topCountry = data?.countries?.[0]?.country_name;
  const chartOption = React.useMemo(() =>
    getCountryFundingChartOption({ data, timeframe, filters } as CountryFundingChartProps),
    [data, timeframe, filters, forceRefresh]
  );

  const titleOption = Array.isArray(chartOption?.title) ? chartOption.title[0] : chartOption?.title;
  const currentTitle = titleOption?.text || '';
  const currentSubtitle = titleOption?.subtext || '';

  // Validate data before processing
  const validation = dataValidationService.validateCountryData(data);
  if (!validation.isValid) {
    console.warn('Country data validation failed:', validation.errors);
  }

  const hasData = data?.countries && data.countries.length > 0;

  if (error) {
    return (
      <div className={ChartContainer.error()}>
        <div className="text-red-600">
          <p className="font-semibold">Error loading country funding data</p>
          <p className="text-sm mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Global Cybersecurity Funding Distribution</h3>
        </div>
        <p className="text-gray-600 text-center py-8">
          No funding data available {data?.week ? `for ${data.week}` : ''}
        </p>
      </div>
    );
  }

  const handleCSVExport = () => {
    const csvContent = countryFundingToCSV(data);
    const filename = generateExportFilename('country-funding', 'csv');
    downloadCSV(csvContent, filename);
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
        {/* Action buttons - hidden on mobile for less crowding */}
        <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
          <button
            onClick={() => setEditorOpen(true)}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {customTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
          <button
            onClick={handleCSVExport}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title="Export data as CSV"
            disabled={isLoading}
          >
            <FileSpreadsheet className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => downloadChart('blog')}
            className="chart-button chart-button-primary text-xs px-2 py-1.5"
            title="Download chart as PNG"
            disabled={isLoading}
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>

        {/* Chart canvas - title is IN the chart, not here */}
        <div
          ref={chartRef}
          className="w-full max-w-full"
          style={{
            width: '100%',
            maxWidth: '1200px', // Constrain to prevent overflow
            height: '800px', // Standardized height for consistency
            minHeight: '700px',
            maxHeight: '1200px'
          }}
        />

        {/* Chart Metadata */}
        {metadata && (
          <ChartMetadata
            periodStart={metadata.current_period?.start}
            periodEnd={metadata.current_period?.end}
            cacheStatus={metadata.cache_status}
            responseTimeMs={metadata.response_time_ms}
          />
        )}
      </div>

      {/* Title Editor Modal */}
      <TitleEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTitle={currentTitle}
        currentSubtitle={currentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(titleKey, title, subtitle);
          setForceRefresh(prev => prev + 1); // useEffect will call refreshChart()
          setEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(titleKey);
          setForceRefresh(prev => prev + 1); // useEffect will call refreshChart()
          setEditorOpen(false);
        }}
        chartContext={{
          chartType: 'country',
          timeframe,
          topCategory: topCountry
        }}
      />
    </>
  );
});

CountryFundingChartComponent.displayName = 'CountryFundingChartComponent';

/**
 * Export the wrapped component
 * The HOC handles all ECharts lifecycle management
 */
export const CountryFundingChartV2 = withChartConfiguration(
  CountryFundingChartComponent,
  getCountryFundingChartOption
);